import React, { useCallback, useEffect, useRef, useState } from "react";
import { Camera, LoaderCircle, RefreshCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLang } from "@/lib/i18n";
import { cn } from "@/lib/utils";

/*
 * Live camera capture ONLY — there is deliberately NO file input anywhere in
 * this application (spec §4.1). Frames come from getUserMedia via a canvas.
 *
 * Client-side face detection here is for FRAMING FEEDBACK ONLY ("centre your
 * face"); the authoritative detection and matching happen server-side on the
 * uploaded image (spec §4.2).
 */

let faceapiPromise = null;
function loadFaceApi() {
  if (!faceapiPromise) {
    faceapiPromise = import("@vladmandic/face-api/dist/face-api.esm.js")
      .then(async (faceapi) => {
        await faceapi.tf.ready();
        await faceapi.nets.tinyFaceDetector.loadFromUri("/models");
        return faceapi;
      })
      .catch(() => null); // graceful degradation: static guide only
  }
  return faceapiPromise;
}

export const CameraCapture = ({ onCapture, capturedBlob, onRetake, disabled }) => {
  const { t } = useLang();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const detectTimer = useRef(null);
  const [camState, setCamState] = useState("starting"); // starting | ready | denied | unavailable
  const [faceOk, setFaceOk] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent);

  const stopStream = useCallback(() => {
    if (detectTimer.current) { clearInterval(detectTimer.current); detectTimer.current = null; }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((tr) => tr.stop());
      streamRef.current = null;
    }
  }, []);

  const startCamera = useCallback(async () => {
    setCamState("starting");
    if (!navigator.mediaDevices?.getUserMedia) {
      setCamState("unavailable");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 960 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      setCamState("ready");
      // framing feedback loop (client-side only, non-authoritative)
      const faceapi = await loadFaceApi();
      if (faceapi && streamRef.current) {
        detectTimer.current = setInterval(async () => {
          const v = videoRef.current;
          if (!v || v.readyState < 2) return;
          try {
            const det = await faceapi.detectSingleFace(
              v, new faceapi.TinyFaceDetectorOptions({ inputSize: 224, scoreThreshold: 0.4 }),
            );
            setFaceOk(Boolean(det));
          } catch (e) { /* feedback only */ }
        }, 900);
      }
    } catch (err) {
      setCamState(err?.name === "NotAllowedError" ? "denied" : "unavailable");
    }
  }, []);

  useEffect(() => {
    if (!capturedBlob) startCamera();
    return stopStream;
  }, [capturedBlob, startCamera, stopStream]);

  useEffect(() => {
    if (!capturedBlob) { setPreviewUrl(null); return undefined; }
    const u = URL.createObjectURL(capturedBlob);
    setPreviewUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [capturedBlob]);

  const capture = useCallback(() => {
    const video = videoRef.current;
    if (!video || video.readyState < 2) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      if (blob) {
        stopStream();
        onCapture(blob);
      }
    }, "image/jpeg", 0.88);
  }, [onCapture, stopStream]);

  // ---- captured preview state ----
  if (capturedBlob && previewUrl) {
    return (
      <div className="relative flex-1 overflow-hidden">
        <img src={previewUrl} alt="captured selfie" className="absolute inset-0 h-full w-full object-cover rk-video-mirror" />
        <div className="absolute bottom-0 inset-x-0 rk-safe-bottom flex justify-center bg-gradient-to-t from-black/60 to-transparent pt-8">
          <Button
            type="button"
            variant="secondary"
            data-testid="camera-retake-button"
            onClick={() => { onRetake(); }}
            disabled={disabled}
            className="h-11"
          >
            <RefreshCcw className="h-4 w-4 mr-2" /> {t("retake")}
          </Button>
        </div>
      </div>
    );
  }

  // ---- error states ----
  if (camState === "denied" || camState === "unavailable") {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-4 px-6 text-center">
        <Camera className="h-10 w-10 text-white/50" />
        <p data-testid="camera-error-message" className="text-sm text-white/90 max-w-sm">
          {camState === "denied"
            ? (isIos ? t("cameraDeniedIos") : t("cameraDenied"))
            : t("cameraUnavailable")}
        </p>
        <Button type="button" variant="secondary" data-testid="camera-retry-button" onClick={startCamera}>
          <RefreshCcw className="h-4 w-4 mr-2" /> {t("retake")}
        </Button>
      </div>
    );
  }

  // ---- live preview ----
  return (
    <div className="relative flex-1 overflow-hidden">
      <video ref={videoRef} playsInline muted autoPlay className="absolute inset-0 h-full w-full object-cover rk-video-mirror" />
      {camState === "starting" && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/60">
          <span className="inline-flex items-center gap-2 text-sm text-white/80">
            <LoaderCircle className="h-4 w-4 animate-spin" /> {t("cameraStarting")}
          </span>
        </div>
      )}
      {/* face guide oval — CSS only, dimmed surround */}
      <div
        className={cn(
          "pointer-events-none absolute left-1/2 top-[44%] h-[46vh] max-h-[400px] w-[70vw] max-w-[300px] -translate-x-1/2 -translate-y-1/2 rounded-[999px] border-2 rk-face-oval transition-colors duration-150",
          faceOk ? "border-emerald-400" : "border-white/70",
        )}
      />
      <div className="pointer-events-none absolute inset-x-0 top-[72%] flex justify-center px-6">
        <span
          data-testid="face-guide-hint"
          className="rounded-full bg-black/50 px-3 py-1 text-xs text-white/90"
        >
          {faceOk ? t("faceLooksGood") : t("centerFace")}
        </span>
      </div>
      <div className="absolute bottom-0 inset-x-0 rk-safe-bottom flex justify-center bg-gradient-to-t from-black/60 to-transparent pt-10">
        <button
          type="button"
          data-testid="camera-capture-button"
          onClick={capture}
          disabled={camState !== "ready" || disabled}
          aria-label={t("capture")}
          className="h-16 w-16 rounded-full border-4 border-white bg-white/25 transition-colors duration-150 active:scale-95 disabled:opacity-40 flex items-center justify-center"
        >
          <span className="h-11 w-11 rounded-full bg-white" />
        </button>
      </div>
    </div>
  );
};
